import { configureStore } from "@reduxjs/toolkit";

import { apiErrorHandler } from "./middleware";
import { accountService } from "./services/account";
import { authService } from "./services/authentication";
import { configService } from "./services/config";
import { forecastsService } from "./services/forecasts";
import { moduleService } from "./services/modules";
import { userService } from "./services/user";
import { userInfoService } from "./services/userInfo";
import { venueService } from "./services/venues";
import { intercomReducer } from "./services/intercom";
import { statusService } from "./services/status";
import { automationsService } from "./services/automations";
import { skillsService } from "./services/skills";

// DE chat services
import {
  conversationService,
  conversationSlice,
  conversationStatusListener,
} from "../features/data-explorer/store/services/conversation";
import { accountFilesService } from "../features/data-explorer/store/services/accountFiles";
import { dashboardService } from "../features/data-explorer/store/services/dashboard";
import { datasetService } from "../features/data-explorer/store/services/datasets";
import { chatSlice } from "../features/data-explorer/store/services/chat/chatSlice";
// DE almanac services (used by MessageRenderer tools and /v2/almanac native route)
import {
  itemService,
  calendarService,
  itemTypeService,
} from "../features/data-explorer/store/services/almanac";
import calendarPreferencesReducer from "../features/data-explorer/store/features/calendarPreferences/calendarPreferencesSlice";
import { calendarPreferencesListener } from "../features/data-explorer/store/features/calendarPreferences/calendarPreferencesListeners";
import itemTypePreferencesReducer from "../features/data-explorer/store/features/itemTypePreferences/itemTypePreferencesSlice";
import { itemTypePreferencesListener } from "../features/data-explorer/store/features/itemTypePreferences/itemTypePreferencesListeners";
import { layoutSlice } from "../features/data-explorer/store/features/layout";
// DE query service (used by useWeatherData inside AlmanacPage)
import { queryService } from "../features/data-explorer/store/services/query";
// DE /v2/data/api services
import { clientCredentialsService } from "./services/clientCredentials";
import { queryHistoryService } from "./services/queryHistory";
// DE /v2/data/data-explorer services
import { catalogService } from "../features/data-explorer/store/services/catalog";
import { fieldService } from "../features/data-explorer/store/services/fields";
import { hermesService } from "../features/data-explorer/store/services/sql";
import { domainMappingService } from "../features/data-explorer/store/services/domainMapping";
import { savedQueryService } from "../features/data-explorer/store/services/savedQuery";
import { threadService } from "../features/data-explorer/store/services/thread";
// DE /v2/data/data-explorer slices
import { selectedDatasetSlice } from "../features/data-explorer/store/features/dataset";
import { drawerSlice } from "../features/data-explorer/store/features/drawer";
import { formSubmissionSlice } from "../features/data-explorer/store/features/drawer/formSubmission";
import { selectedFieldSlice } from "../features/data-explorer/store/features/field";
import { selectedSavedQuerySlice } from "../features/data-explorer/store/features/savedQuery";

export const store = configureStore({
  reducer: {
    [configService.reducerPath]: configService.reducer,
    [userInfoService.reducerPath]: userInfoService.reducer,
    [authService.reducerPath]: authService.reducer,
    [moduleService.reducerPath]: moduleService.reducer,
    [userService.reducerPath]: userService.reducer,
    [accountService.reducerPath]: accountService.reducer,
    [venueService.reducerPath]: venueService.reducer,
    [statusService.reducerPath]: statusService.reducer,
    [forecastsService.reducerPath]: forecastsService.reducer,
    [automationsService.reducerPath]: automationsService.reducer,
    [skillsService.reducerPath]: skillsService.reducer,
    intercom: intercomReducer,
    // DE services
    [conversationService.reducerPath]: conversationService.reducer,
    [accountFilesService.reducerPath]: accountFilesService.reducer,
    [dashboardService.reducerPath]: dashboardService.reducer,
    [datasetService.reducerPath]: datasetService.reducer,
    [itemService.reducerPath]: itemService.reducer,
    [calendarService.reducerPath]: calendarService.reducer,
    [itemTypeService.reducerPath]: itemTypeService.reducer,
    [clientCredentialsService.reducerPath]: clientCredentialsService.reducer,
    [queryHistoryService.reducerPath]: queryHistoryService.reducer,
    [queryService.reducerPath]: queryService.reducer,
    [catalogService.reducerPath]: catalogService.reducer,
    [fieldService.reducerPath]: fieldService.reducer,
    [hermesService.reducerPath]: hermesService.reducer,
    [domainMappingService.reducerPath]: domainMappingService.reducer,
    [savedQueryService.reducerPath]: savedQueryService.reducer,
    [threadService.reducerPath]: threadService.reducer,
    // DE slices
    chat: chatSlice.reducer,
    conversationStatus: conversationSlice.reducer,
    calendarPreferences: calendarPreferencesReducer,
    itemTypePreferences: itemTypePreferencesReducer,
    layout: layoutSlice.reducer,
    selectedDataset: selectedDatasetSlice.reducer,
    drawer: drawerSlice.reducer,
    formSubmission: formSubmissionSlice.reducer,
    selectedField: selectedFieldSlice.reducer,
    selectedSavedQuery: selectedSavedQuerySlice.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      apiErrorHandler,
      authService.middleware,
      configService.middleware,
      userInfoService.middleware,
      moduleService.middleware,
      userService.middleware,
      accountService.middleware,
      venueService.middleware,
      statusService.middleware,
      forecastsService.middleware,
      automationsService.middleware,
      skillsService.middleware,
      // DE middleware
      conversationService.middleware,
      accountFilesService.middleware,
      dashboardService.middleware,
      datasetService.middleware,
      itemService.middleware,
      calendarService.middleware,
      itemTypeService.middleware,
      clientCredentialsService.middleware,
      queryHistoryService.middleware,
      queryService.middleware,
      catalogService.middleware,
      fieldService.middleware,
      hermesService.middleware,
      domainMappingService.middleware,
      savedQueryService.middleware,
      threadService.middleware,
      conversationStatusListener.middleware,
      calendarPreferencesListener.middleware,
      itemTypePreferencesListener.middleware,
    ),
  devTools: import.meta.env.MODE !== "production",
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
